import { task } from 'mobx-task'
import Workspace from 'iam/models/Workspace'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { CreateBundleParams } from 'admin/bundle/dtos/dtos'
import { BundleStore } from 'admin/bundle/BundleStore'
import WorkspaceSelectorViewModel from 'admin/bundle/WorkspaceSelectorForBundlesViewModel'
import InventoryStore from 'admin/InventoryStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import { mapIncludedItemVmToIds } from 'admin/bundle/dtos/bundleGrpcDtoMapper'
import { IWithWorkspaceSelectionForBundles } from 'admin/bundle/components/BundleFormWorkpaceDropdown'
import { required, validationContext } from 'validx'
import ModalViewModel from 'infra/ModalViewModel'
import ManageItemsInBundleViewModel, {
  IncludedItemViewModel,
} from 'admin/bundle/components/ManageItemsInBundleViewModel/ManageItemsInBundleViewModel'
import { PriceEditorViewModel } from '../PriceEditorViewModel'
import { lengthBetween } from 'utils/validation-utils'

export default class CreateBundleViewModel
  extends ModalViewModel
  implements IWithWorkspaceSelectionForBundles
{
  constructor(
    public inventoryItemStore: InventoryStore,
    private bundleStore: BundleStore,
    private refreshBundles: RefreshBundles,
    public getSelectedWorkspace: () => Workspace | undefined,
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * The name for the Bundle.
   */
  @observable
  name = ''

  /**
   * The description for the Bundle.
   */
  @observable
  description = ''

  /**
   * The Bundle price.
   */
  @observable
  price = 0

  /**
   * The included items for the Bundle.
   */
  @observable
  includedItems: IncludedItemViewModel[] = []

  /**
   * The base inventory item id for the Bundle.
   */
  @observable
  baseItemId = ''

  /**
   * The selected workspace
   */
  @observable
  workspace? = Workspace.none

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
  } as const

  manageItemsInBundleViewModel = new ManageItemsInBundleViewModel(
    this.setIncludedItems,
    this.inventoryItemStore,
  )

  /**
   * The Bundle price editor view model.
   */
  readonly pricingVM = new PriceEditorViewModel()

  /**
   * Validation context for this view model.
   */
  validation = validationContext(this, {
    name: [
      required('A name is required.'),
      lengthBetween(
        this.fieldLengths.name.min,
        this.fieldLengths.name.max,
        `Name must be between ${this.fieldLengths.name.min} and ${this.fieldLengths.name.max} characters.`,
      ),
    ],
    description: [
      required('A description is required.'),
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `Description must be between ${this.fieldLengths.description.min} and ${this.fieldLengths.description.max} characters.`,
      ),
    ],
  })

  /**
   * Validations for the workspace
   */
  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  /**
   * Returns a sorted copy of the included items list, ordered by their names.
   *
   * @return {Array} The sorted copy of included items, ordered by name.
   */
  @computed
  get orderedIncludedItems(): IncludedItemViewModel[] {
    const baseItem = this.includedItems.find((i) => i.isBaseItem)
    const allButBaseItem = this.includedItems
      .filter((i) => !i.isBaseItem)
      .sort((a, b) => a.name.localeCompare(b.name))

    if (baseItem) {
      return [baseItem, ...allButBaseItem]
    }

    return allButBaseItem
  }

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      `${max - current} characters remaining`

    return {
      name: makeIndicator(this.fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this.fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
    this.manageItemsInBundleViewModel.setWorkspaceId(this.workspace?.id || '')
  }

  /**
   * Shows the dialog
   */
  @override
  show() {
    this.setWorkspace(this.getSelectedWorkspace())
    super.show()
  }

  /**
   * Resets elements involved in the creation process
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.price = 0
    this.includedItems = []
    this.baseItemId = ''
    this.manageItemsInBundleViewModel.baseItemId = ''
    this.manageItemsInBundleViewModel.includedItems = []

    this.submit.reset()
    this.validation.reset()
    this.pricingVM.reset()
  }

  @action
  open() {
    this.reset()
    this.manageItemsInBundleViewModel.activate()
    this.show()
  }

  @action.bound
  setIncludedItems(includedItems: IncludedItemViewModel[]) {
    this.baseItemId =
      includedItems.find((item) => item.isBaseItem)?.inventoryItemId || ''
    this.manageItemsInBundleViewModel.setBaseItemId(this.baseItemId)
    this.includedItems = includedItems
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingVM.validate()

    return base && pricing
  }

  /**
   * Creates the bundle
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid ||
      this.workspace === undefined
    ) {
      return
    }

    if (this.baseItemId === '') {
      this.newAlert()
        .withState(AlertState.Warning)
        .saying('Base item is required', 'Please include a base item.')
        .autoDismiss()
        .show()
      return
    }

    const includedItemsProto = this.includedItems.filter((i) => !i.isBaseItem)

    this.includedItems = includedItemsProto

    const price = this.pricingVM.toValue()

    const payload: CreateBundleParams = {
      name: this.name,
      description: this.description,
      baseItemId: this.baseItemId,
      includedItemsIds: mapIncludedItemVmToIds(
        this.includedItems.filter(
          (item) => item.inventoryItemId !== this.baseItemId,
        ),
      ),
      price,
      workspaceId: this.workspace.id!,
    }

    try {
      await this.bundleStore.create(payload)
    } catch (e) {
      this.newAlert()
        .withState(AlertState.Error)
        .saying('Error', e.message)
        .autoDismiss()
        .show()

      this.close()
      return
    }

    // Refresh the list to show new bundles
    await this.refreshBundles(this.workspace || Workspace.none)

    this.validation.reset()

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success', 'Bundle created.')
      .autoDismiss()
      .show()

    this.close()
  })
}
